import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search, LogOut, LayoutDashboard, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { DwinovLogo } from "./DwinovLogo";

const NAV = [
  { to: "/explore", label: "Explore" },
  { to: "/categories", label: "Categories" },
  { to: "/ai", label: "AI Tools" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" }
];

const Avatar = ({ user }) => {
  if (user?.avatar_url) return <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />;
  const initial = (user?.name || user?.email || "U").trim().charAt(0).toUpperCase();
  return <span className="text-sm font-semibold">{initial}</span>;
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { user, openAuth, logout, requireAuth } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleStart = () => {
    if (!requireAuth("Sign in to launch your campaign.")) return;
    navigate("/start");
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 backdrop-blur-xl bg-cream-100/85 border-b transition-colors ${scrolled ? "border-warm-200" : "border-transparent"}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" data-testid="navbar-logo" className="flex items-center group">
            <div className="transition-transform group-hover:scale-[1.04]">
              <DwinovLogo size={40} />
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to} to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    isActive ? "text-saffron-700 bg-saffron-50" : "text-navy-600 hover:text-navy-800 hover:bg-warm-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => navigate("/explore")}
              data-testid="navbar-search-btn"
              className="grid place-items-center h-10 w-10 rounded-xl text-navy-600 hover:bg-warm-100 hover:text-navy-800 transition-colors"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>

            {user ? (
              <>
                <Button
                  variant="ghost" onClick={handleStart}
                  data-testid="navbar-start-campaign-btn"
                  className="rounded-2xl text-navy-700 hover:bg-warm-100 font-semibold"
                >
                  Start a campaign
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="navbar-user-menu" className="h-10 w-10 rounded-full bg-navy-800 text-white grid place-items-center overflow-hidden hover:scale-105 transition-transform" aria-label="Account menu">
                      <Avatar user={user} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                    <DropdownMenuLabel>
                      <div className="font-medium truncate">{user.name}</div>
                      <div className="text-xs text-navy-500 truncate">{user.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/creator/${user.user_id}`)}>
                      <User className="h-4 w-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
                      <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant="ghost" onClick={() => openAuth("login")}
                  data-testid="navbar-signin-btn"
                  className="rounded-2xl text-navy-700 hover:bg-warm-100 font-semibold"
                >
                  Sign in
                </Button>
                <Button
                  onClick={handleStart}
                  data-testid="navbar-start-campaign-btn"
                  className="btn-saffron rounded-2xl font-semibold"
                >
                  Start a campaign
                </Button>
              </>
            )}
          </div>

          <button
            className="md:hidden grid place-items-center h-10 w-10 rounded-xl text-navy-700 hover:bg-warm-100"
            onClick={() => setOpen(!open)}
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 border-t border-warm-200 pt-4 space-y-1" data-testid="mobile-menu">
            {NAV.map((item) => (
              <NavLink
                key={item.to} to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-xl text-sm font-semibold ${isActive ? "text-saffron-700 bg-saffron-50" : "text-navy-700 hover:bg-warm-100"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-2 flex gap-2">
              {user ? (
                <>
                  <Button variant="ghost" onClick={() => { navigate("/dashboard"); setOpen(false); }} className="flex-1 rounded-2xl">Dashboard</Button>
                  <Button onClick={() => { handleLogout(); setOpen(false); }} className="flex-1 btn-saffron rounded-2xl">Sign out</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => { openAuth("login"); setOpen(false); }} className="flex-1 rounded-2xl">Sign in</Button>
                  <Button onClick={() => { handleStart(); setOpen(false); }} className="flex-1 btn-saffron rounded-2xl">Start</Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
